import React, { useEffect, useRef, useState } from 'react';

const phrases = [
  'оптимизируют процессы.',
  'увеличивают прибыль.',
  'сокращают рутину.',
  'связывают системы.',
];

const holdDelay = 2200;
const eraseDelay = 35;
const emptyDelay = 300;
const typeDelay = 75;

type Phase = 'holding' | 'erasing' | 'empty' | 'typing';

const RotatingHeroText: React.FC = () => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [frame, setFrame] = useState({ phrase: 0, length: phrases[0].length });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let phrase = 0;
    let { length } = phrases[0];
    let phase: Phase = 'holding';
    let inView = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let remainingDelay = holdDelay;
    let deadline = 0;

    const pause = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
        remainingDelay = Math.max(0, deadline - performance.now());
      }
    };

    const schedule = () => {
      if (timer !== undefined || !inView || document.hidden || motionPreference.matches) {
        return;
      }

      deadline = performance.now() + remainingDelay;
      timer = setTimeout(() => {
        timer = undefined;

        if (phase === 'holding' || phase === 'erasing') {
          length -= 1;
          phase = length === 0 ? 'empty' : 'erasing';
          remainingDelay = length === 0 ? emptyDelay : eraseDelay;
        } else if (phase === 'empty') {
          phrase = (phrase + 1) % phrases.length;
          length = 1;
          phase = 'typing';
          remainingDelay = typeDelay;
        } else {
          length += 1;
          const complete = length === phrases[phrase].length;
          phase = complete ? 'holding' : 'typing';
          remainingDelay = complete ? holdDelay : typeDelay;
        }

        setFrame({ phrase, length });
        schedule();
      }, remainingDelay);
    };

    const updateVisibility = () => {
      if (!inView || document.hidden || motionPreference.matches) {
        pause();
      } else {
        schedule();
      }
    };

    const updateMotionPreference = () => {
      if (motionPreference.matches) {
        pause();
        phrase = 0;
        length = phrases[0].length;
        phase = 'holding';
        remainingDelay = holdDelay;
        setFrame({ phrase, length });
      }
      updateVisibility();
    };

    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      updateVisibility();
    });

    observer.observe(container);
    motionPreference.addEventListener('change', updateMotionPreference);
    document.addEventListener('visibilitychange', updateVisibility);

    return () => {
      pause();
      observer.disconnect();
      motionPreference.removeEventListener('change', updateMotionPreference);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  const words = phrases[frame.phrase].split(' ');

  return (
    <span
      ref={containerRef}
      className="hero-rotating"
    >
      <span className="sr-only">{phrases[0]}</span>
      <span
        aria-hidden="true"
        className="hero-rotating-visual"
      >
        {phrases.map((phrase) => (
          <span
            key={phrase}
            className="hero-rotating-measure"
          >
            {phrase}
          </span>
        ))}
        <span className="hero-rotating-text">
          {words.map((word, index) => {
            const start = words.slice(0, index).join(' ').length + (index > 0 ? 1 : 0);
            const visibleLength = Math.max(0, Math.min(word.length, frame.length - start));
            const hasCaret = frame.length >= start && frame.length <= start + word.length;

            return (
              <React.Fragment key={word}>
                {index > 0 ? ' ' : null}
                <span className="hero-rotating-word">
                  {word.slice(0, visibleLength)}
                  {hasCaret && <span className="hero-rotating-caret" />}
                  <span className="hero-rotating-rest">{word.slice(visibleLength)}</span>
                </span>
              </React.Fragment>
            );
          })}
        </span>
      </span>
    </span>
  );
};

export default RotatingHeroText;
